/**
 * Header navigation — [Org] › [Browse / Section ▾]
 *
 *   Left crumb   the org context (org name), static — image 1/7.
 *   Page crumb   "Browse" (compass) when the current route is a left-sidebar
 *                page; otherwise the active section's name — images 5/6 vs 1-4.
 *   Panel        two panes: the four sections on the left, and the hovered/active
 *                section's pages on the right — images 1-4.
 *
 * Sections and their pages come from useTopicNav, which applies the same
 * permission + module gating as the sidebar, so the two navs always agree on
 * what the user may open.
 */
import { useEffect, useRef, useState }    from "react";
import { NavLink, useNavigate }           from "react-router-dom";
import styles                             from "./TopNav.module.scss";
import { useTopicNav, type TopicSection } from "./useTopicNav";
import { ClientSelector }                 from "./ClientSelector";
import { OrgSelector }                    from "./OrgSelector";
import { useAuth }                        from "@features/auth/AuthContext";
import type { NavItem }                   from "@app/navigation/nav";

/** Portal target for page-level controls in the top bar. */
export const TOPNAV_SLOT_ID = "topnav-slot";

export const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sections, activeItem, activeSection } = useTopicNav();

  // Single state for the whole bar's open menu — "browse" (the sections
  // panel), "client" (the agent picker dropdown), or null (none open).
  // Having one state instead of two independent booleans guarantees the
  // two menus are mutually exclusive — opening either one implicitly
  // closes the other, in both directions.
  type OpenMenu = "browse" | "client" | "org" | null;
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const open = openMenu === "browse";
  const [preview, setPreview] = useState<TopicSection | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // One click-outside / escape handler governs BOTH menus. Clicking inside
  // the TopNav root (browse panel OR client dropdown) is "inside"; anywhere
  // else closes whatever is open.
  useEffect(() => {
    if (openMenu === null) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpenMenu(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenMenu(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  // Viewers see no topic sections but still need the bar to host their
  // single-destination link in the right corner. Only bail when both are
  // absent (any role with neither sections nor viewer status has nothing
  // the bar can offer).
  const isViewer = user?.role === "viewer";
  if (sections.length === 0 && !isViewer) return null;

  const go = (item: NavItem) => {
    setOpenMenu(null);
    if (!item.soon) navigate(item.to);
  };

  const openPanel = () => {
    setPreview(activeSection ?? sections[0] ?? null);
    setOpenMenu((m) => (m === "browse" ? null : "browse"));
  };

  // First crumb — scope-aware label.
  //
  // The authenticated user may be scoped at platform / group / org level
  // (see SCOPE_RANK in useTopicNav). Each scope has its own "home" name
  // on the profile — org_name for org admins, group_name for group admins
  // — and the crumb should show whichever matches. Previously the label
  // only ever read org_name, so group admins (viewing /organizations etc.
  // where there's no bound org) saw the literal fallback "Organization".
  //
  // Resolution order:
  //   platform → "Platform" (no entity-level name to show)
  //   group    → user.group_name  ?? localStorage("specta.group_name")  ?? "Group"
  //   org      → user.org_name    ?? localStorage("specta.org_name")    ?? "Organization"
  //
  // The localStorage fallbacks let the crumb render the real name
  // instantly on reload without waiting for the /me API round-trip —
  // same pattern AuthContext uses to persist org_name today. The
  // `(user as …)` casts are defensive since User type may not yet
  // expose group_name formally; drop them once it does.
  const safeGet = (k: string): string | null => {
    try { return localStorage.getItem(k); } catch { return null; }
  };
  const scope = user?.scope_level ?? "org";
  const groupName  = (user as { group_name?: string } | null | undefined)?.group_name;
  const orgLabel =
    scope === "platform"
      ? "Platform"
      : scope === "group"
        ? (groupName ?? safeGet("specta.group_name") ?? "Group")
        : (user?.org_name ?? safeGet("specta.org_name") ?? "Organization");

  const orgIcon =
    scope === "platform"
      ? "fa-layer-group"
      : scope === "group"
        ? "fa-layer-group"
        : "fa-building";

  // Show the specific active page's name in the crumb when the user is on a
  // topnav page (e.g. "File Integrity Monitoring"), not the parent section
  // ("Monitoring"). On sidebar pages we fall back to "Browse" + compass so the
  // switcher still invites exploration.
  const onTopicPage = activeItem != null && activeSection != null;
  // The ClientSelector picks an AGENT, so it should be HIDDEN on pages that
  // are explicitly scoped to a group or the platform — those pages don't
  // operate on a single agent, and showing an empty selector between two
  // chevrons produces the "double chevron" visual bug. The previous attempt
  // at this fix gated on `scope === "agent"`, which was too strict: most
  // agent-scoped topic pages (System Monitoring, Vulnerability Detection, CIS,
  // Threat Hunting, Auth & Access, Agent Dashboard) declare scope "org" or
  // "agent" inconsistently, so only FIM — the one page with an explicit
  // "agent" scope — kept its selector. Switching to a deny-list fixes
  // that regression: hide for the two known non-agent scopes, show for
  // everything else (including "org" and "agent"). If a new scope value
  // is added later that also shouldn't show ClientSelector, extend this
  // array — the opt-out is cheap, the opt-in default is safe.
  // Hide the agent (client) selector when the current page lives in the
  // sidebar (Organizations, Group Dashboard) — those are group-level
  // pages with no concept of a selected agent — or when the page is
  // explicitly scoped above the agent level ("platform"). "group" is
  // NO LONGER a scope-based deny because the sidebar flag now carries
  // that responsibility for the two pages that need it.
  const hideSelectorScopes: Array<string | undefined> = ["platform"];
  const onAgentPage =
    onTopicPage &&
    !hideSelectorScopes.includes(activeItem.scope) &&
    !activeItem.sidebar;
  // Group admins get an OrgSelector before the ClientSelector — but NOT
  // on Organizations / Group Dashboard themselves. Those pages are the
  // group admin's launchpad; no organization has been chosen yet, and
  // the picker appears naturally once they navigate into an org-scoped
  // page via Browse (AutoSelectOrg fills in row 0 at that point).
  // Hides for other scopes too (platform has no single-org concept;
  // org admins already have a fixed org on their JWT).
  const showOrgSelector = scope === "group" && !activeItem?.sidebar;
  // Note: Browse stays visible on sidebar pages too (Organizations /
  // Group Dashboard). It's how the group admin navigates OUT of those
  // launchpads into the rest of the app. The sidebar flag only drives
  // OrgSelector + ClientSelector hiding, not Browse.
  const crumbLabel = onTopicPage ? activeItem.label : "Browse";
  const crumbIcon = onTopicPage ? activeItem.icon : "fa-compass";
  const crumbHue = onTopicPage ? activeSection.hue : undefined;

  return (
    <div className={styles.topnav} ref={rootRef}>
      <div className={styles.bar}>
        {/* org context */}
        <span className={`${styles.crumb} ${styles.crumbOrg}`}>
          {/* <i className={`fas fa-layer-group ${styles.crumbIcon}`} />
          
          */}
          <i className={`fas ${orgIcon} ${styles.crumbIcon}`} />
          <span>{orgLabel}</span>
        </span>
        <i className={`fas fa-chevron-right ${styles.sep}`} />

        {/* Viewer-only Endpoint Inventory crumb — sits BEFORE Org / Agent /
            Browse so the viewer's breadcrumb starts cleanly with their
            single destination right after the Group crumb:
                Group › Endpoint Inventory ›
            Includes its own `>` separator so the chain reads consistently
            with the other crumbs (each of which pairs itself with a
            trailing chevron). */}
        {isViewer && (
          <>
            <NavLink
              to="/endpoint-inventory"
              title="Endpoint Inventory"
              aria-label="Endpoint Inventory"
              className={({ isActive }) =>
                `${styles.crumb} ${styles.crumbPage} ${isActive ? styles.crumbOpen : ""}`
              }
            >
              <i className={`far fa-rectangle-list ${styles.crumbIcon}`} />
              <span>Endpoint Inventory</span>
            </NavLink>
            <i className={`fas fa-chevron-right ${styles.sep}`} />
          </>
        )}

        {/* Organization selector — group admins only. Lets a Group Admin
            switch between the orgs in their group. Sits between the Group
            crumb and the ClientSelector so the breadcrumb reads:
                Group › Org ▾ › Agent ▾ › Section
            For org admins the user already has a baked-in org on the JWT
            and this component is hidden (same for platform). */}
        {showOrgSelector && (
          <>
            <OrgSelector
              isOpen={openMenu === "org"}
              onOpenChange={(next) => setOpenMenu(next ? "org" : null)}
            />
            <i className={`fas fa-chevron-right ${styles.sep}`} />
          </>
        )}

        {/* Client (agent) selector — only when the current page is actually
            agent-scoped. Previously this gated on onTopicPage (true for ANY
            nav-registered route) which left an empty ClientSelector slot
            between the org crumb and the Browse button on org/group pages,
            rendering as two chevrons with a gap. */}
        {onAgentPage && (
          <>
            {/* ClientSelector runs in controlled mode here so TopNav can
                coordinate with the Browse panel — opening one closes the
                other. If ClientSelector is used elsewhere without these
                props, it falls back to its own internal state. */}
            <ClientSelector
              isOpen={openMenu === "client"}
              onOpenChange={(next) => setOpenMenu(next ? "client" : null)}
            />
            <i className={`fas fa-chevron-right ${styles.sep}`} />
          </>
        )}

        {/* section / browse switcher — not shown for viewers (they have no
            topic sections to switch between; their single destination is
            rendered as the Endpoint Inventory crumb above, right after the
            Group separator). Wrapped in a relative container so the dropdown
            panel can anchor directly under the button rather than the far
            left of the bar. */}
        {sections.length > 0 && (
          <div className={styles.browseWrap}>
            <button
              className={`${styles.crumb} ${styles.crumbPage} ${open ? styles.crumbOpen : ""}`}
              data-hue={crumbHue}
              onClick={openPanel}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <i className={`fas ${crumbIcon} ${styles.crumbIcon}`} />
              <span>{crumbLabel}</span>
              <i className={`fas fa-chevron-${open ? "up" : "down"} ${styles.caret}`} />
            </button>
            {open && (
              <div className={styles.panel} role="menu">
                {/* panel contents moved here from the previous location
                    (end of .topnav). Rendering next to the Browse button
                    anchors it directly under the trigger. */}
                <PanelInner
                  sections={sections}
                  preview={preview}
                  setPreview={setPreview}
                  activeSection={activeSection}
                  activeItem={activeItem}
                  go={go}
                />
              </div>
            )}
          </div>
        )}

        {/* page-level controls portal into this by id */}
        <div className={styles.slot} id={TOPNAV_SLOT_ID} />

        {/* The right-corner viewer link lived here until it was moved
            inline into the crumb chain above. Its .viewerLink /
            .viewerLinkIcon / .viewerLinkActive styles in TopNav.module.scss
            are no longer referenced — safe to delete in a follow-up
            cleanup pass. Left for now so a quick revert is a single-file
            change. */}
      </div>

    </div>
  );
};

/**
 * Panel body — two panes side by side. Lifted out of the main component so
 * the Browse-button-anchored instance stays readable, and so a future
 * compact / mobile layout can reuse it.
 */
interface PanelInnerProps {
  sections: TopicSection[];
  preview: TopicSection | null;
  setPreview: (s: TopicSection) => void;
  activeSection: TopicSection | undefined;
  activeItem: NavItem | undefined;
  go: (item: NavItem) => void;
}
const PanelInner: React.FC<PanelInnerProps> = ({
  sections, preview, setPreview, activeSection, activeItem, go,
}) => (
  <>
    {/* left: the four sections */}
    <div className={styles.paneSections}>
      <div className={styles.paneHead}>Sections</div>
      {sections.map((s) => {
        const isPreview = (preview ?? activeSection)?.topic === s.topic;
        return (
          <button
            key={s.topic}
            className={`${styles.sectionBtn} ${isPreview ? styles.sectionActive : ""}`}
            data-hue={s.hue}
            onMouseEnter={() => setPreview(s)}
            onFocus={() => setPreview(s)}
            onClick={() => setPreview(s)}
          >
            <span className={styles.sectionIcon}>
              <i className={`fas ${s.icon}`} />
            </span>
            <span className={styles.sectionLabel}>{s.label}</span>
            <span className={styles.sectionCount}>{s.items.length}</span>
            <i className={`fas fa-chevron-right ${styles.sectionArrow}`} />
          </button>
        );
      })}
    </div>

    {/* right: the previewed section's pages */}
    <div className={styles.panePages}>
      {(() => {
        const shown = preview ?? activeSection ?? sections[0];
        if (!shown) return null;
        return (
          <>
            <div className={styles.panePagesHead} data-hue={shown.hue}>
              {shown.label}
            </div>
            {shown.items.map((item) => (
              <button
                key={item.id}
                className={`${styles.pageBtn} ${activeItem?.id === item.id ? styles.pageActive : ""} ${item.soon ? styles.pageSoon : ""}`}
                data-hue={shown.hue}
                onClick={() => go(item)}
                disabled={item.soon}
                title={item.soon ? "Coming soon" : item.label}
              >
                <span className={styles.pageIcon}>
                  <i className={`fas ${item.icon}`} />
                </span>
                <span>{item.label}</span>
                {item.soon && <span className={styles.soonTag}>Soon</span>}
                {activeItem?.id === item.id && (
                  <i className={`fas fa-circle-check ${styles.pageCheck}`} />
                )}
              </button>
            ))}
          </>
        );
      })()}
    </div>
  </>
);
